import { checkFeatureFlagGate } from "@wac-web/utils/feature-flags/check-feature-flag";
import { featureFlags } from "@wac-web/utils/feature-flags/feature-flag-manifest";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";

import BookIcon from "@atlaskit/icon/core/book-with-bookmark";
import CompassIcon from "@atlaskit/icon/core/compass";
import PersonIcon from "@atlaskit/icon/core/person-avatar";
import WorkItemsIcon from "@atlaskit/icon/core/work-items";

import { useRoutes } from "@/common/hooks";
import { FASTSHIFT_PATH_KEYS } from "@/common/routes/constants";
import { getDomainScopedPathKeys } from "@/common/routes/utils";
import { useFastshiftSelector } from "@/state";

import { messages } from "../messages";

type NavLinkItem = {
    key: FASTSHIFT_PATH_KEYS;
    label: string;
    icon: React.ReactElement;
    link: string | null;
};

export const usePageNavLinks = () => {
    const { fullPathname, paths } = useRoutes();
    const { formatMessage } = useIntl();
    const { domainId } = useParams<{ domainId: string }>();

    const { data: selectedDomainData } = useFastshiftSelector(
        (state) => state.customerDomain
    );

    const { data: domainsTableList } = useFastshiftSelector(
        (state) => state.customerDomains
    );

    const isUnder1kExperienceEnabled = checkFeatureFlagGate(
        featureFlags.sxpFastshiftPortalUnder1kCustomerExperience
    );

    // note that the order of this navLinks array determines
    // the order of links displayed inside the sidenav component
    const navLinks: NavLinkItem[] = [
        // (Ordered) Partner Scoped Links
        {
            key: FASTSHIFT_PATH_KEYS.SELECTION,
            label: formatMessage(messages.customersNavLabel),
            link: paths.selection,
            icon: <PersonIcon label="" />
        },
        {
            key: FASTSHIFT_PATH_KEYS.RESOURCES_PARTNER_SCOPED,
            label: formatMessage(messages.resourcesNavLabel),
            link: paths.partnerResources,
            icon: <BookIcon label="" />
        },
        // (Ordered) Domain Scoped Links
        {
            key: FASTSHIFT_PATH_KEYS.OVERVIEW,
            label: formatMessage(messages.forYouNavLabel),
            link: paths.domainScopedOverview,
            icon: <PersonIcon label="" />
        },
        {
            key: FASTSHIFT_PATH_KEYS.TICKETS,
            label: formatMessage(messages.migrationTicketsNavLabel),
            link: paths.domainScopedTickets,
            icon: <WorkItemsIcon label="" />
        },
        {
            key: FASTSHIFT_PATH_KEYS.PLANNING,
            label: formatMessage(messages.migrationPlanningNavLabel),
            link: paths.domainScopedPlanning,
            icon: <CompassIcon label="" />
        },
        {
            key: FASTSHIFT_PATH_KEYS.RESOURCES_DOMAIN_SCOPED,
            label: formatMessage(messages.resourcesNavLabel),
            link: paths.domainScopedResources,
            icon: <BookIcon label="" />
        }
    ];

    // find the key of the navlinks object with a link that matches the pathname
    const currentPageKey: FASTSHIFT_PATH_KEYS | undefined = navLinks.find(
        (navLink) => navLink.link === fullPathname
    )?.key as FASTSHIFT_PATH_KEYS;

    // filter and return domain scoped sidenav
    // if customer is small (under 1k), do not show "Migration Planning" navlink
    const domainScopedPathKeys = getDomainScopedPathKeys();

    const userSelectedAnOver1kCustomerFromSelectionPage =
        domainsTableList?.data?.find(
            (domain) => domain.customerDomainId === domainId
        )?.hasOver1KPaidSeatsInAtLeastOneProduct;

    const shouldShowMigrationPlanningLink =
        selectedDomainData?.hasOver1KPaidSeatsInAtLeastOneProduct ||
        userSelectedAnOver1kCustomerFromSelectionPage;

    const domainScopedLinks = navLinks.filter(
        (navLink) =>
            domainScopedPathKeys.includes(navLink.key) &&
            (navLink.key !== FASTSHIFT_PATH_KEYS.PLANNING ||
                shouldShowMigrationPlanningLink)
    );

    if (domainScopedPathKeys.includes(currentPageKey || "")) {
        return {
            pageKey: currentPageKey,
            links: isUnder1kExperienceEnabled
                ? domainScopedLinks
                : [
                      ...navLinks.filter((navLink) =>
                          domainScopedPathKeys.includes(navLink.key)
                      )
                  ],
            isOnDomainScopedPage: true
        };
    }

    // return partner scoped sidenav
    return {
        pageKey: currentPageKey,
        links: [
            ...navLinks.filter(
                (navLink) => !domainScopedPathKeys.includes(navLink.key)
            )
        ],
        isOnDomainScopedPage: false
    };
};
